import React, { useMemo } from 'react'
import { Card, CardContent } from '@duck/ui'
import { useAssignments } from '@/hooks/useAssignments'
import { usePersons } from '@/hooks/usePersons'
import { CohortPersonDetails } from '../cohortPageData'
import { PartnerService, UserService } from '@duck/api-definitions'
import { useTaskDisplayNames } from '@/modules/entityLookups'
import { usePartnerTar } from '@/hooks/usePartnerTar'
import { AssignmentEntry } from './AssignmentEntry'
import { NoDataEmptyState } from '@/components/EmptyStates'
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline'
import { AssignmentsListSkeleton } from './AssignmentsListSkeleton'
import { useTar } from '@/modules'

interface AssignmentsListProps {
  assignmentsDetails?: PartnerService.AssignmentDetails[]
  onAssignmentChange: () => void
  skeleton?: boolean
}

const toCohortPersonDetails = (
  assignmentDetail: PartnerService.AssignmentDetails,
  person: UserService.Person
): CohortPersonDetails => ({
  personId: assignmentDetail.personId,
  email: person.email,
  onboardingStatus: person.onboardingStatus,
  createdAt: assignmentDetail.createdAt,
  assignmentStatus: assignmentDetail.status,
  challengeId: assignmentDetail.challengeId,
  grade: assignmentDetail.grade,
  feedback: assignmentDetail.feedback,
  solution: assignmentDetail.solution,
  startDate: assignmentDetail.startDate,
  submitDate: assignmentDetail.submitDate,
  workTime: assignmentDetail.workTime,
  charts: assignmentDetail.charts,
  queries: assignmentDetail.queries,
})

export const AssignmentsList: React.FC<AssignmentsListProps> = ({
  onAssignmentChange,
  assignmentsDetails = [],
  skeleton = false
}) => {
  const { tP } = usePartnerTar()
  const { t } = useTar()

  // Get personIds from assignmentsDetails (avoids circular dependency)
  const personIdsFromDetails = useMemo(
    () => Array.from(new Set(assignmentsDetails.map((ad) => ad.personId))),
    [assignmentsDetails]
  )
  const { persons, isLoading: isLoadingPersons } = usePersons(personIdsFromDetails)

  // Build assignmentsByAssignmentId directly from API data to ensure no fields (e.g. queries) are lost
  const assignmentsByAssignmentId = useMemo(() => {
    if (!assignmentsDetails.length || !persons.length) return {}
    const personsMap = new Map(persons.map((p) => [p.id, p]))
    const result: Record<string, CohortPersonDetails[]> = {}
    for (const ad of assignmentsDetails) {
      const person = personsMap.get(ad.personId)
      if (!person) continue
      if (!result[ad.assignmentId]) result[ad.assignmentId] = []
      result[ad.assignmentId].push(toCohortPersonDetails(ad, person))
    }
    return result
  }, [assignmentsDetails, persons])

  const assignmentIds = useMemo(() => Object.keys(assignmentsByAssignmentId), [assignmentsByAssignmentId])
  const { assignments: assignmentsData, isLoading: isLoadingAssignments, refetch: refetchAssignments } = useAssignments(assignmentIds)
  const assignments: Record<string, PartnerService.Assignment> = assignmentsData

  const nonPublicAssignmentIds = useMemo(
    () =>
      assignmentIds.filter(assignmentId => {
        const assignment = assignments[assignmentId]
        return assignment && !assignment.linkToken
      }),
    [assignmentIds, assignments]
  )

  // Get taskIds for display names
  const taskIds = useMemo(() => {
    return Array.from(new Set(Object.values(assignments).map(a => a.taskId)))
  }, [assignments])

  const { getDisplayName, isLoading: isTaskLoading } = useTaskDisplayNames(taskIds, {
    fallback: t('common.na')
  })


  if (skeleton) {
    return <AssignmentsListSkeleton variant="detailed" />
  }
  const isLoading = isLoadingAssignments || isLoadingPersons

  if (isLoading) {
    return <AssignmentsListSkeleton variant="simple" />
  }

  if (nonPublicAssignmentIds.length === 0) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="pt-6">
            <NoDataEmptyState
              icon={ClipboardDocumentListIcon}
              title={tP('noAssignmentsYet')}
              description={tP('noAssignmentsDescription')}
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {nonPublicAssignmentIds.map(assignmentId => {
        const assignment = assignments[assignmentId]
        const peopleDetails = assignmentsByAssignmentId[assignmentId] || []
        const personIds = peopleDetails.map(pd => pd.personId)
        const assignmentPersons = persons.filter(p => personIds.includes(p.id))

        return (
          <div key={assignmentId}>
            <AssignmentEntry
              assignment={assignment}
              peopleDetails={peopleDetails}
              assignmentPersons={assignmentPersons}
              getDisplayName={getDisplayName}
              isTaskLoading={isTaskLoading}
              onAssignmentChange={() => {
                refetchAssignments()
                onAssignmentChange()
              }}
            />
          </div>
        )
      })}
    </div>
  )
}
